import {
	Component, Input, Output,
	EventEmitter, ChangeDetectorRef, ElementRef, ViewChild
} from '@angular/core';

import {
	createValueAccessorProviders,
	ValueAccessorBase
} from '@cs/components/shared';

/**
 * Represents the different states that require custom transitions between them.
 * @docs-private
 */
export const enum CsTransitionCheckState {
	/** The initial state of the component before any user interaction. */
	Init,
	/** The state representing the component when it's becoming checked. */
	Checked,
	/** The state representing the component when it's becoming unchecked. */
	Unchecked,
	/** The state representing the component when it's becoming indeterminate. */
	Indeterminate,
}


/**
 * Ui checkbox component.
 *

 */
@Component({
						 selector:    'cs-checkbox',
						 templateUrl: './checkbox.component.html',
						 providers:   [createValueAccessorProviders(CsCheckboxComponent)],
						 standalone: false
					 })
export class CsCheckboxComponent extends ValueAccessorBase<boolean> {

	/** Name value will be applied to the input element if present */
	@Input() name: string | null = null;
	/**
	 * Indicate a explicit valid state
	 */
	@Input() isValid: boolean;
	/**
	 * indicate a invalid state
	 */
	@Input() isInValid: boolean;

	/** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
	@Input() id: string;

	/** Event emitted when the checkbox's `checked` value changes. */
	@Output() readonly change: EventEmitter<boolean> = new EventEmitter<boolean>();

	/** The native `<input type="checkbox">` element */
	@ViewChild('input') _inputElement: ElementRef<HTMLInputElement>;
	_disabled: boolean;

	/** Whether the checkbox is checked. */
	@Input()
	get checked(): boolean {
		return this._checked;
	}

	set checked(value: boolean) {
		const checked = value;

		if (checked != this.checked) {
			this._checked = checked;
			this._changeDetectorRef.markForCheck();
		}
	}

	@Input() set disabled(v: boolean) {
		this._disabled = v !== false;
	}

	/** Whether the checkbox is required. */
	@Input()
	get required(): boolean {
		return this._required;
	}

	set required(value: boolean) {
		this._required = value;
	}

	/** Returns the unique id for the visual hidden input. */
	get inputId(): string {
		return `${this.id || this._uniqueId}-input`;
	}

	get disabled() {
		return this._disabled;
	}

	constructor(private _changeDetectorRef: ChangeDetectorRef) {super(); }

	_onBlur() {
		// When a focused element becomes disabled, the browser *immediately* fires a blur event.
		// Angular does not expect events to be raised during change detection, so any state change
		// (such as a form control's 'ng-touched') will cause a changed-after-checked error.
		// See https://github.com/angular/angular/issues/17793. To work around this, we defer
		// telling the form control it has been touched until the next tick.
		Promise.resolve()
					 .then(() => {
						 this._onTouched();
						 this._changeDetectorRef.markForCheck();
					 });
	}

	_onInputClick() {
		this._handleInputClick();
	}

	_onInteractionEvent(event: Event) {
		// We always have to stop propagation on the change event.
		// Otherwise the change event, from the input element, will bubble up and
		// emit its event object to the `change` output.
		event.stopPropagation();
	}

	protected _handleInputClick() {


		// If resetIndeterminate is false, and the current state is indeterminate, do nothing on click
		if (!this.disabled) {


			this._checked = !this._checked;


			// Emit our custom change event if the native input emitted one.
			// It is important to only emit it, if the native input triggered one, because
			// we don't want to trigger a change event, when the `checked` variable changes for example.
			this._emitChangeEvent();
		} else if (!this.disabled) {
			// Reset native input when clicked with noop. The native checkbox becomes checked after
			// click, reset it to be align with `checked` value of `mat-checkbox`.
			this._inputElement.nativeElement.checked = this.checked;

		}
	}


	private _checked = false;

	private _required: boolean;

	private _uniqueId: string;

	private _emitChangeEvent() {
		this._controlValueAccessorChangeFn(this.checked);
		this.change.emit(this.checked);

		// Assigning the value again here is redundant, but we have to do it in case it was
		// changed inside the `change` listener which will cause the input to be out of sync.
		if (this._inputElement) {
			this._inputElement.nativeElement.checked = this.checked;
		}
	}
}
